import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ScrollableDialog from '../common/ScrollableDialog';
import PrimaryActionButton from '../common/PrimaryActionButton';
import { formatCurrency } from '../../utils/currencyUtils';
import { formatUtcDate } from '../../utils/dateFormatUtils';

const AssignExistingTeamMemberConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  memberName,
  email,
  billingImpact,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setLoading(false);
    }
  }, [open]);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm();
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const renderBillingNotice = () => {
    if (!billingImpact?.willIncurAdditionalCharge) {
      return null;
    }

    const currency = (billingImpact.currency || 'EUR').toUpperCase();
    const recurringLabel = formatCurrency(billingImpact.recurringMonthlyIncrease, currency);
    const periodEndLabel = billingImpact.currentPeriodEndUtc
      ? formatUtcDate(billingImpact.currentPeriodEndUtc)
      : null;

    if (billingImpact.isTrial) {
      return (
        <Alert severity="info" sx={{ mt: 2 }}>
          {t('teamMembers.billing.trialNotice', { price: recurringLabel })}
        </Alert>
      );
    }

    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        {periodEndLabel
          ? t('teamMembers.billing.assignExistingNotice', {
            amount: recurringLabel,
            date: periodEndLabel,
          })
          : t('teamMembers.billing.assignExistingNoticeNoDate', { amount: recurringLabel })}
      </Alert>
    );
  };

  return (
    <ScrollableDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      title={t('teamMembers.registration.emailCheck.existingTitle')}
      dividers
      showCloseButton
      titleSx={{ pb: 1, flexShrink: 0 }}
      actionsSx={{
        flexShrink: 0,
        justifyContent: 'space-between',
      }}
      actions={(
        <>
          <Button onClick={onClose} color="inherit" disabled={loading}>
            {t('common.cancel')}
          </Button>
          <PrimaryActionButton onClick={handleConfirm} disabled={loading}>
            {t('teamMembers.registration.emailCheck.existingConfirm')}
          </PrimaryActionButton>
        </>
      )}
    >
      <Box>
        <Typography variant="body1">
          {t('teamMembers.registration.emailCheck.existingMessage', { name: memberName, email })}
        </Typography>
        {renderBillingNotice()}
      </Box>
    </ScrollableDialog>
  );
};

export default AssignExistingTeamMemberConfirmDialog;
